import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import BlofinLiveOrderPanel from './BlofinLiveOrderPanel';
import BitunixLiveOrderPanel from './BitunixLiveOrderPanel';

const COLORS = {
  ink: '#17323c',
  muted: '#4f646f',
  line: 'rgba(23,50,60,0.12)',
  paper: '#ffffff',
  page: 'linear-gradient(180deg, #efe8dc 0%, #edf3f6 100%)',
};

const EXCHANGES = [
  {
    id: 'BLOFIN',
    label: 'BloFin',
    mode: 'LIVE manuel',
    detail: 'Préparation d’intention, confirmation explicite, suivi SL/TP et réconciliation protégée.',
  },
  {
    id: 'BITUNIX',
    label: 'Bitunix',
    mode: 'LIVE manuel',
    detail: 'Préparation d’intention, confirmation explicite, suivi SL/TP et réconciliation protégée.',
  },
];

export default function CryptoOrdersAdminPage() {
  const [exchange, setExchange] = useState('BLOFIN');
  const selected = EXCHANGES.find((item) => item.id === exchange) || EXCHANGES[0];

  const changeExchange = (next) => {
    if (next && next !== exchange) {
      setExchange(next);
    }
  };

  return (
    <Box
      data-testid="crypto-orders-page"
      sx={{
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        '&, & *': { boxSizing: 'border-box' },
        p: { xs: 1.5, md: 3 },
        background: COLORS.page,
        minHeight: '100%',
      }}
    >
      <Stack spacing={{ xs: 1.5, md: 2.5 }} sx={{ width: '100%', maxWidth: 1660, mx: 'auto', minWidth: 0 }}>
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            minWidth: 0,
            p: { xs: 2, md: 3 },
            borderRadius: 5,
            background: 'linear-gradient(135deg, #17323c 0%, #355b63 100%)',
            color: '#f7f3ea',
          }}
        >
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1.5} alignItems={{ md: 'center' }}>
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Chip label="ADMIN" sx={{ bgcolor: 'rgba(247,243,234,0.14)', color: '#f7f3ea', fontWeight: 800 }} />
                <Chip label="Crypto orders" sx={{ bgcolor: 'rgba(243,198,92,0.18)', color: '#f3c65c', fontWeight: 800 }} />
              </Stack>
              <Typography variant="h4" sx={{ fontWeight: 900, overflowWrap: 'anywhere' }}>
                Ordres crypto
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(247,243,234,0.78)', maxWidth: 900 }}>
                Interface d’administration unifiée. Chaque exchange conserve ses API, ses comptes,
                ses validateurs et ses garde-fous. La sélection d’un exchange ne déclenche aucune mutation.
              </Typography>
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignSelf: { xs: 'stretch', md: 'auto' } }}>
              <Button
                href="/trading/blofin"
                variant="outlined"
                sx={{ color: '#f7f3ea', borderColor: 'rgba(247,243,234,0.45)', whiteSpace: 'nowrap' }}
              >
                Route BloFin historique
              </Button>
              <Button
                href="/trading/stats"
                variant="contained"
                sx={{ bgcolor: '#f3c65c', color: '#17323c', fontWeight: 800, whiteSpace: 'nowrap', '&:hover': { bgcolor: '#e7b94f' } }}
              >
                Statistiques trading
              </Button>
            </Stack>
          </Stack>
        </Paper>

        <Paper elevation={0} sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 5, bgcolor: COLORS.paper, border: `1px solid ${COLORS.line}`, minWidth: 0 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ md: 'center' }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" sx={{ color: COLORS.muted, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                Exchange
              </Typography>
              <Box sx={{ mt: 0.6 }}>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={exchange}
                  onChange={(_, next) => changeExchange(next)}
                  aria-label="Sélection de l’exchange"
                >
                  {EXCHANGES.map((item) => (
                    <ToggleButton key={item.id} value={item.id} sx={{ px: 2, fontWeight: 800 }}>
                      {item.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip size="small" label={selected.mode} color={exchange === 'BLOFIN' ? 'success' : 'default'} />
              <Typography variant="body2" sx={{ color: COLORS.muted, minWidth: 0 }}>{selected.detail}</Typography>
            </Stack>
          </Stack>
        </Paper>

        <Alert severity={exchange === 'BLOFIN' ? 'info' : 'warning'}>
          {exchange === 'BLOFIN'
            ? 'BloFin LIVE : la préparation d’une intention ne déclenche aucun ordre. La confirmation explicite et les vérifications backend restent obligatoires.'
            : 'Bitunix LIVE : la préparation d’une intention ne déclenche aucun ordre. La confirmation explicite et les vérifications backend restent obligatoires.'}
        </Alert>

        {exchange === 'BLOFIN' ? (
          <Box data-testid="crypto-orders-blofin">
            <BlofinLiveOrderPanel />
          </Box>
        ) : (
          <Box data-testid="crypto-orders-bitunix">
            <BitunixLiveOrderPanel />
          </Box>
        )}
      </Stack>
    </Box>
  );
}
